import React, { useState, useRef, useEffect } from 'react';
import { useAppStore } from '@/store/app';

interface Message {
  role: 'user' | 'assistant';
  text: string;
}

const CANNED: Record<string, string> = {
  reportes: '📊 Esta semana hay **23 reportes** activos: 8 en proceso, 12 pendientes, 3 resueltos. Las categorías más frecuentes son Vialidad (9) y Baches (7).',
  sector: '📍 El sector con más incidencias es **El Paraíso Centro** con 6 hallazgos activos, seguido de Las Mercedes con 4.',
  prioridad: '⚠️ Hay **3 reportes de alta prioridad** pendientes esta semana: 2 de vialidad y 1 de alumbrado.',
  default: '🤖 Soy el asistente IA de ComuniData. Puedo ayudarte a consultar estadísticas, filtrar reportes, identificar zonas críticas y más. ¿Qué necesitas?',
};

function getResponse(q: string): string {
  const lower = q.toLowerCase();
  if (lower.includes('reporte') || lower.includes('inciden')) return CANNED.reportes;
  if (lower.includes('sector') || lower.includes('zona') || lower.includes('parroq')) return CANNED.sector;
  if (lower.includes('prior') || lower.includes('urgent') || lower.includes('alta')) return CANNED.prioridad;
  return CANNED.default;
}

export default function AssistantPanel() {
  const searchOpen = useAppStore((s) => s.searchOpen);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', text: '¡Hola! Soy tu asistente IA. ¿En qué puedo ayudarte hoy?' },
  ]);
  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function send() {
    const text = input.trim();
    if (!text) return;
    const userMsg: Message = { role: 'user', text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setTimeout(() => {
      setMessages(prev => [...prev, { role: 'assistant', text: getResponse(text) }]);
    }, 600);
  }

  if (!searchOpen) return null;

  return (
    <div style={{
      position: 'absolute', bottom: 90, left: '50%', transform: 'translateX(-50%)',
      width: 'min(480px, calc(100vw - 32px))',
      background: 'rgba(10,22,40,0.92)', backdropFilter: 'blur(20px)',
      borderRadius: 20, border: '1px solid rgba(96,165,250,0.2)',
      boxShadow: '0 16px 40px rgba(0,0,0,0.4)',
      zIndex: 35, overflow: 'hidden', animation: 'popIn 200ms ease-out',
    }}>
      {/* Header */}
      <div style={{ padding: '14px 16px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#1D4ED8,#60A5FA)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>
          <i className="ti ti-sparkles" style={{ color: '#fff' }} />
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Asistente IA</span>
        <span style={{ fontSize: 10, color: 'rgba(96,165,250,0.6)', marginLeft: 4, background: 'rgba(96,165,250,0.1)', padding: '2px 7px', borderRadius: 20 }}>Beta</span>
      </div>

      {/* Messages */}
      <div style={{ maxHeight: 260, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '82%', padding: '9px 12px', borderRadius: m.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: m.role === 'user' ? 'rgba(29,78,216,0.5)' : 'rgba(255,255,255,0.07)',
              fontSize: 12.5, color: '#fff', lineHeight: 1.5,
            }}>
              {m.text}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Suggestions */}
      <div style={{ padding: '0 16px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {['¿Cuántos reportes hay?', 'Zona más crítica', 'Alta prioridad'].map(s => (
          <button key={s} onClick={() => { setInput(s); }} style={{ padding: '5px 10px', borderRadius: 20, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.2)', color: 'rgba(96,165,250,0.8)', fontSize: 10.5, fontWeight: 600, cursor: 'pointer' }}>
            {s}
          </button>
        ))}
      </div>

      {/* Input */}
      <div style={{ padding: '0 12px 14px', display: 'flex', gap: 8 }}>
        <input
          value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && send()}
          placeholder="Pregunta algo…"
          style={{ flex: 1, padding: '10px 14px', borderRadius: 12, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: 13, outline: 'none' }}
        />
        <button onClick={send} style={{ width: 40, height: 40, borderRadius: 12, background: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, flexShrink: 0 }}>
          <i className="ti ti-send" />
        </button>
      </div>
    </div>
  );
}
